/**
 * Eligibility detail right rail: actions plus lazy status and run history.
 */

import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import type { PatientScreeningResult } from "../../api/clinicalTrialsApiTypes";
import NoData from "../../components/noData/noData";
import type { EligibilityRecommendedAction } from "../../data-model/eligibilityDetail";
import { useEligibilityScreeningHistory } from "../../hooks/useEligibilityHistories";
import { formatDisplayDate } from "../../utils/displayDate";
import { formatSnakeCaseEnumLabel } from "../../utils/stringModifications";
import { useEligibilityDetailContext } from "./EligibilityDetailContext";
import { EligibilitySourceLink } from "./shared/EligibilitySourceLink";
import { questionDisplayText } from "./shared/screeningStatusDisplay";
import { StatusHistoryPanel } from "./shared/StatusHistoryPanel";

type RightRailView = "actions" | "status-history" | "run-history";

/**
 * Action heading: protocol question label when the detail payload has it, otherwise the id.
 *
 * @param screening - Latest screening from the detail response
 * @param action - Recommended action being rendered
 * @returns Heading shown above the action text
 */
function actionHeading(
  screening: PatientScreeningResult | null,
  action: EligibilityRecommendedAction,
): string {
  const question = screening?.stages
    ?.flatMap((stage) => stage.questions)
    .find((item) => item.question_id === action.question_id);
  const text = question ? questionDisplayText(question) : "";
  return `${text || action.question_id} · ${action.atom_id}`;
}

/**
 * Render server-provided recommended actions and durable overrides.
 *
 * @returns Default right-rail content
 */
function ActionsView() {
  const { detail } = useEligibilityDetailContext();
  return (
    <div className="flex flex-col gap-5">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-primaryGray-1">
          Recommended CRC Actions ({detail.recommended_actions.length})
        </h3>
        <div className="flex flex-col gap-3">
          {detail.recommended_actions.length === 0 && (
            <p className="rounded bg-primaryGray-16 p-3 text-xs text-primaryGray-4">
              No recommended actions.
            </p>
          )}
          {detail.recommended_actions.map((action) => (
            <article
              key={action.action_id}
              data-action-id={action.action_id}
              className="rounded-lg border border-primaryGray-14 p-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">
                  {actionHeading(detail.latest_screening, action)}
                </span>
                <Tags label={action.status} />
              </div>
              <p className="text-xs leading-5 text-primaryGray-3">
                {action.action}
              </p>
              <p className="mt-2 text-overline text-primaryGray-7">
                {action.action_id}
              </p>
            </article>
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-3 text-sm font-semibold text-primaryGray-1">
          Overrides ({detail.overrides.length})
        </h3>
        <div className="flex flex-col gap-3">
          {detail.overrides.length === 0 && (
            <p className="text-xs text-primaryGray-4">No CRC overrides.</p>
          )}
          {detail.overrides.map((override) => (
            <article
              key={override.atom_id}
              className="rounded-lg border border-primaryGray-14 p-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">{override.atom_id}</span>
                <Tags
                  label={override.verdict}
                  backgroundColor="#E8F1FF"
                  textColor="#0056D6"
                />
              </div>
              <p className="text-xs leading-5 text-primaryGray-3">
                {override.comment}
              </p>
              <EligibilitySourceLink
                sourcePath={override.justification_source_path}
                label="View justification"
              />
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * Render complete run history after the user explicitly opens it.
 *
 * @returns Lazy screening-run history content
 */
function RunHistoryView() {
  const { trialId, mrn } = useEligibilityDetailContext();
  const state = useEligibilityScreeningHistory(trialId, mrn, true);
  if (state.loading) return <p className="text-xs">Loading run history…</p>;
  if (state.error) return <NoData text={state.error} />;
  if (!state.data?.runs.length) {
    return <p className="text-xs text-primaryGray-4">No screening runs.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {state.data.runs.map((run) => (
        <article
          key={run.run_id || run.triggered_at}
          className="rounded-lg border border-primaryGray-14 p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold">
              {formatSnakeCaseEnumLabel(run.verdict || run.status, "—")}
            </span>
            <Tags
              label={
                run.eligibility_tier
                  ? formatSnakeCaseEnumLabel(run.eligibility_tier)
                  : "No tier"
              }
            />
          </div>
          <p className="mt-2 break-all text-overline text-primaryGray-7">
            {run.run_id || "No run id"}
          </p>
          <p className="text-overline text-primaryGray-7">
            {formatDisplayDate(run.triggered_at)} · {run.triggered_by}
          </p>
        </article>
      ))}
    </div>
  );
}

/**
 * Right rail with detail-backed actions and opt-in history sub-views.
 *
 * @returns Right rail
 */
const RightPanel = () => {
  const { trialId, mrn } = useEligibilityDetailContext();
  const [view, setView] = useState<RightRailView>("actions");
  return (
    <aside className="flex w-1/4 flex-col overflow-hidden border-l border-primaryGray-14 bg-white">
      <div className="flex border-b border-primaryGray-14 p-2">
        {(["actions", "status-history", "run-history"] as RightRailView[]).map(
          (item) => (
            <button
              key={item}
              type="button"
              className={`flex-1 rounded px-2 py-2 text-x-tiny font-semibold ${
                view === item
                  ? "bg-[#EAF2FF] text-tertiaryBlue-4"
                  : "text-primaryGray-4"
              }`}
              onClick={() => setView(item)}
            >
              {item === "actions"
                ? "Actions"
                : item === "status-history"
                  ? "Status"
                  : "Runs"}
            </button>
          ),
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {view === "actions" && <ActionsView />}
        {view === "status-history" && (
          <StatusHistoryPanel trialId={trialId} mrn={mrn} active />
        )}
        {view === "run-history" && <RunHistoryView />}
      </div>
    </aside>
  );
};

export default RightPanel;
